import { SorterModel } from "@enxcs/dap-ui-stratos";
import { useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type CustomParamsType = {
  page: number;
  onChangePage(page: number): void;
  rowsPerPage: number;
  onChangeRowsPerPage(rowsPerPage: number): void;
  searchTerm: string;
  onChangeSearchTerm(searchTerm: string): void;
  sorting: SorterModel | undefined;
  onChangeSorting(sorting: SorterModel | undefined): void;
  orderByColumn: string;
  isAscending: boolean;
};

type locationStateType = {
  state_page: number;
  state_rowsPerPage: number;
  state_searchTerm: string;
  state_sorting: SorterModel | undefined;
};

type PersistedParams = {
  rowsPerPage: number;
  searchTerm: string;
  sorting: SorterModel | undefined;
};

const sorterTableIndex = ["title", "date", "duration", "status"];

const PERSISTED_PARAMS_KEY = "recordsListParams";

const defaultPersistedParams: PersistedParams = { rowsPerPage: 10, searchTerm: "", sorting: undefined };

function readPersistedParams(): PersistedParams {
  try {
    const raw = sessionStorage.getItem(PERSISTED_PARAMS_KEY);
    return raw ? { ...defaultPersistedParams, ...JSON.parse(raw) } : defaultPersistedParams;
  } catch {
    return defaultPersistedParams;
  }
}

function writePersistedParams(updates: Partial<PersistedParams>) {
  try {
    sessionStorage.setItem(PERSISTED_PARAMS_KEY, JSON.stringify({ ...readPersistedParams(), ...updates }));
  } catch {
    // sessionStorage unavailable (e.g. private browsing) - fall back to in-memory state only
  }
}

export function useCustomParams(): CustomParamsType {
  const location = useLocation();

  const navigate = useNavigate();

  const persistedParams = readPersistedParams();

  const {
    state_page = 1,
    state_rowsPerPage = persistedParams.rowsPerPage,
    state_searchTerm = persistedParams.searchTerm,
    state_sorting = persistedParams.sorting,
  } = location.state || {};

  const orderByColumn = state_sorting ? sorterTableIndex[state_sorting.index] : "";

  const isAscending = state_sorting ? state_sorting.value === "ascending" : false;

  const locationRef = useRef(location);
  locationRef.current = location;

  const { state, pathname, search, hash } = locationRef.current;

  const updateLocationState = useCallback(
    (updates: Partial<locationStateType>) => {
      if ("state_rowsPerPage" in updates || "state_searchTerm" in updates || "state_sorting" in updates) {
        writePersistedParams({
          ...("state_rowsPerPage" in updates ? { rowsPerPage: updates.state_rowsPerPage } : {}),
          ...("state_searchTerm" in updates ? { searchTerm: updates.state_searchTerm } : {}),
          ...("state_sorting" in updates ? { sorting: updates.state_sorting } : {}),
        });
      }

      navigate(
        { pathname, search, hash },
        {
          state: {
            ...(state || {}),
            ...updates,
          },
          replace: true,
        },
      );
    },
    [navigate, state, hash, search, pathname],
  );

  const onChangePage = useCallback(
    (newPage: number) => {
      if (newPage === state?.state_page) return;
      updateLocationState({ state_page: newPage });
    },
    [updateLocationState, state?.state_page],
  );

  const onChangeRowsPerPage = useCallback(
    (newRowsPerPage: number) => {
      if (newRowsPerPage === state?.state_rowsPerPage) return;
      updateLocationState({ state_rowsPerPage: newRowsPerPage, state_page: 1 });
    },
    [updateLocationState, state?.state_rowsPerPage],
  );

  const onChangeSearchTerm = useCallback(
    (newSearchTerm: string) => {
      if (newSearchTerm === state?.state_searchTerm) return;
      updateLocationState({ state_searchTerm: newSearchTerm, state_page: 1 });
    },
    [updateLocationState, state?.state_searchTerm],
  );

  const onChangeSorting = useCallback(
    (newSorting?: SorterModel) => {
      updateLocationState({ state_sorting: newSorting, state_page: 1 });
    },
    [updateLocationState],
  );

  return {
    page: state_page,
    onChangePage,
    rowsPerPage: state_rowsPerPage,
    onChangeRowsPerPage,
    searchTerm: state_searchTerm,
    onChangeSearchTerm,
    sorting: state_sorting,
    onChangeSorting,
    orderByColumn,
    isAscending,
  };
}
